import React, { useState } from 'react';
import { Outlet, NavLink, useLocation } from 'react-router-dom';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../hooks/useAuth';
import { guideHref } from '../utils/guideLink';
import Logo from '../assets/logo';
import NotificationIcon from '../components/common/NotificationIcon';
import LanguageSwitcher from '../components/common/LanguageSwitcher';
import AppFooter from '../components/common/AppFooter';

// Styled components for layout
const LayoutContainer = styled.div`
  display: flex;
  min-height: 100vh;
  background-color: var(--app-bg);
`;

// Sidebar
const Sidebar = styled.aside`
  width: 260px;
  background-color: var(--app-sidebar);
  color: var(--text-inverse);
  display: flex;
  flex-direction: column;
  transition: transform var(--dur-media) var(--ease-control);
  position: fixed;
  top: 0;
  left: 0;
  bottom: 0;
  z-index: 100;
  /* The rail paints above the header (z-index 99); clip it so nothing inside can
     ever spill onto the header bar or the page title. */
  overflow: hidden;

  @media (max-width: 768px) {
    transform: translateX(${({ isOpen }) => (isOpen ? '0' : '-100%')});
    width: 240px;
  }
`;

/* The red masthead plate that carries the white wordmark, as in the admin rail. */
const SidebarHeader = styled.div`
  padding: 14px 20px;
  min-height: 76px;
  display: flex;
  align-items: center;
  background-color: var(--app-sidebar-plate);
  border-bottom: none;
`;

// The lockup is a raster <img> (825x170), so it must be sized here — the old
// `svg { ... }` rule matched nothing and let the logo render 194px wide.
const SidebarLogo = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 4px;
  min-width: 0;
  max-width: 100%;

  img,
  svg {
    height: 34px;
    width: auto;
    max-width: 100%;
    display: block;
  }
`;

// The app name is a subtitle UNDER the wordmark, never a second title beside it.
const SidebarLogoText = styled.div`
  font-family: ${({ theme }) => theme.typography.fontFamily.heading};
  font-size: 11px;
  font-weight: ${({ theme }) => theme.typography.fontWeight.bold};
  line-height: 1;
  letter-spacing: 0.5px;
  text-transform: uppercase;
  color: rgba(255, 255, 255, 0.85);
  margin-left: 0;
  max-width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const SidebarContent = styled.div`
  flex: 1;
  padding: ${({ theme }) => theme.spacing[4]} 0;
  overflow-y: auto;
`;

const SidebarFooter = styled.div`
  padding: 15px 20px;
  border-top: 1px solid var(--border-inverse);
  font-size: var(--text-xs);
  font-weight: var(--weight-regular);
  color: rgba(255, 255, 255, 0.5);
  text-align: center;
`;

// Navigation menu
const NavMenu = styled.nav`
  margin-bottom: ${({ theme }) => theme.spacing[4]};
`;

const NavSection = styled.div`
  margin-bottom: ${({ theme }) => theme.spacing[4]};
`;

const NavSectionTitle = styled.div`
  font-size: var(--text-2xs);
  font-weight: var(--weight-bold);
  text-transform: uppercase;
  letter-spacing: 1px;
  color: rgba(255, 255, 255, 0.38);
  padding: 0 20px 10px;
`;

/* The current page is GREEN — the brand's own "you are here" rule. Nothing in
   the rail is a button, so the green reads as state, not as an approve action. */
const NavItem = styled(NavLink)`
  display: flex;
  align-items: center;
  padding: 11px 20px;
  color: var(--app-nav-idle);
  text-decoration: none;
  font-size: var(--text-base);
  font-weight: var(--weight-regular);
  transition: all var(--dur-control) var(--ease-control);
  border-left: 3px solid transparent;

  &:hover {
    background-color: rgba(255, 255, 255, 0.05);
    color: var(--app-nav-hover);
  }

  &.active {
    background-color: rgba(255, 255, 255, 0.06);
    color: var(--app-nav-active);
    font-weight: var(--weight-bold);
    border-left-color: var(--app-nav-active);
  }

  &:focus-visible {
    outline: none;
    box-shadow: inset var(--focus-ring-accent);
  }

  svg {
    margin-right: 12px;
    width: 18px;
    height: 18px;
  }

  @media (max-width: 768px) {
    padding: 14px 20px;
  }

  /* Touch has no hover, so the green affordance must fire on press instead. */
  @media (hover: none) {
    &:hover {
      background-color: transparent;
      color: var(--app-nav-idle);
    }

    &:active {
      background-color: rgba(255, 255, 255, 0.05);
      color: var(--app-nav-hover);
    }
  }
`;

// Content area
const Content = styled.div`
  flex: 1;
  margin-left: 260px;
  min-width: 0;

  @media (max-width: 768px) {
    margin-left: 0;
  }
`;

const Header = styled.header`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: ${({ theme }) => `${theme.spacing[3]} ${theme.spacing[6]}`};
  background-color: var(--app-surface);
  /* The brand puts no elevation on chrome — a hairline, not a drop shadow. */
  border-bottom: 1px solid var(--app-hairline);
  box-shadow: none;
  position: sticky;
  top: 0;
  z-index: 99;

  @media (max-width: 768px) {
    padding: ${({ theme }) => `${theme.spacing[3]} ${theme.spacing[3]}`};
  }
`;

const PageTitle = styled.h1`
  font-size: var(--text-2xl);
  font-weight: var(--weight-bold);
  text-transform: uppercase;
  letter-spacing: var(--tracking-none);
  line-height: var(--leading-tight);
  color: var(--app-heading);
  margin: 0;

  @media (max-width: 768px) {
    font-size: var(--text-md);
  }
`;

const UserInfo = styled.div`
  display: flex;
  align-items: center;
`;

const UserName = styled.div`
  margin-right: ${({ theme }) => theme.spacing[3]};
  font-size: var(--text-base);
  font-weight: var(--weight-regular);
  color: var(--app-text);

  @media (max-width: 576px) {
    display: none;
  }
`;

const UserMenu = styled.div`
  position: relative;
`;

const UserMenuButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background-color: ${({ theme }) => theme.colors.primary};
  color: white;
  border: none;
  cursor: pointer;
  font-weight: ${({ theme }) => theme.typography.fontWeight.bold};

  &:hover {
    background-color: ${({ theme }) => theme.colors.primaryDark};
  }
`;

const UserMenuDropdown = styled.div`
  position: absolute;
  top: 100%;
  right: 0;
  margin-top: ${({ theme }) => theme.spacing[2]};
  width: 200px;
  background-color: ${({ theme }) => theme.colors.white};
  box-shadow: ${({ theme }) => theme.shadows.md};
  border-radius: ${({ theme }) => theme.borders.radius.md};
  overflow: hidden;
  z-index: 10;
  display: ${({ isOpen }) => (isOpen ? 'block' : 'none')};
`;

const UserMenuItem = styled.button`
  display: block;
  width: 100%;
  text-align: left;
  padding: ${({ theme }) => theme.spacing[3]};
  background-color: transparent;
  color: ${({ theme }) => theme.colors.dark};
  border: none;
  border-bottom: 1px solid ${({ theme }) => theme.colors.border};
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  cursor: pointer;
  transition: ${({ theme }) => theme.transitions.default};

  &:hover {
    background-color: ${({ theme }) => theme.colors.light};
  }

  &:last-child {
    border-bottom: none;
  }

  &.logout {
    color: ${({ theme }) => theme.colors.error};
  }
`;

const MainContent = styled.main`
  padding: ${({ theme }) => theme.spacing[6]};
  background-color: var(--app-bg);
  min-height: calc(100vh - 70px); /* Adjust based on header height */

  @media (max-width: 768px) {
    padding: ${({ theme }) => `${theme.spacing[4]} ${theme.spacing[3]}`};
    /* Clear the fixed bottom tab bar (56px + breathing room + safe area) */
    padding-bottom: calc(76px + env(safe-area-inset-bottom, 0px));
  }
`;

// Bottom tab bar (mobile only)
const BottomTabBar = styled.nav`
  display: none;

  @media (max-width: 768px) {
    display: flex;
    position: fixed;
    left: 0;
    right: 0;
    bottom: 0;
    height: calc(56px + env(safe-area-inset-bottom, 0px));
    padding-bottom: env(safe-area-inset-bottom, 0px);
    background-color: var(--app-surface);
    border-top: 1px solid var(--app-hairline);
    box-shadow: 0 -1px 3px rgba(0, 0, 0, 0.08);
    z-index: 98;
  }
`;

const TabItem = styled(NavLink)`
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 2px;
  min-height: 56px;
  padding: ${({ theme }) => `${theme.spacing[1]} ${theme.spacing[1]}`};
  color: var(--app-muted);
  text-decoration: none;
  font-size: 0.65rem;
  font-weight: ${({ theme }) => theme.typography.fontWeight.medium};
  text-align: center;

  svg {
    width: 22px;
    height: 22px;
    flex-shrink: 0;
  }

  /* On white the current tab stays blue: the rail's green needs a dark ground. */
  &.active {
    color: var(--act-primary);
  }
`;

const TabLabel = styled.span`
  max-width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

// Mobile menu toggle
const MobileMenuToggle = styled.button`
  display: none;
  background: none;
  border: none;
  border-radius: var(--app-radius-control);
  color: var(--app-heading);
  cursor: pointer;
  padding: ${({ theme }) => theme.spacing[2]};
  margin-right: ${({ theme }) => theme.spacing[2]};

  &:hover {
    background: var(--surface-subtle);
    color: var(--act-primary);
    box-shadow: none;
  }

  &:focus-visible {
    outline: none;
    box-shadow: var(--focus-act);
  }

  svg {
    width: 24px;
    height: 24px;
  }

  @media (max-width: 768px) {
    display: block;
  }
`;

// Overlay for mobile
const Overlay = styled.div`
  display: none;

  @media (max-width: 768px) {
    display: ${({ isOpen }) => (isOpen ? 'block' : 'none')};
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background-color: rgba(0, 0, 0, 0.5);
    z-index: 99;
  }
`;

// Dashboard icon
const DashboardIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <rect x="3" y="3" width="7" height="7"></rect>
    <rect x="14" y="3" width="7" height="7"></rect>
    <rect x="14" y="14" width="7" height="7"></rect>
    <rect x="3" y="14" width="7" height="7"></rect>
  </svg>
);

// Employees icon
const EmployeesIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"></path>
    <circle cx="9" cy="7" r="4"></circle>
    <path d="M23 21v-2a4 4 0 0 0-3-3.87"></path>
    <path d="M16 3.13a4 4 0 0 1 0 7.75"></path>
  </svg>
);

// Reports icon
const ReportsIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
    <path d="M14 2v6h6"></path>
    <path d="M16 13H8"></path>
    <path d="M16 17H8"></path>
    <path d="M10 9H8"></path>
  </svg>
);

// Approval icon
const ApprovalIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="9 11 12 14 22 4"></polyline>
    <path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11"></path>
  </svg>
);

// Company icon
const CompanyIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"></path>
    <polyline points="9 22 9 12 15 12 15 22"></polyline>
  </svg>
);

// Menu icon for mobile toggle
const MenuIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <line x1="3" y1="12" x2="21" y2="12"></line>
    <line x1="3" y1="6" x2="21" y2="6"></line>
    <line x1="3" y1="18" x2="21" y2="18"></line>
  </svg>
);

const CustomerLayout = () => {
  const { t, i18n } = useTranslation();
  const { currentUser } = useAuth();
  const location = useLocation();
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);

  // Get page title based on current route
  const getPageTitle = () => {
    const path = location.pathname;

    if (path.includes('/customer/dashboard')) return t('nav.customer.dashboard');
    if (path.includes('/customer/employees')) return t('nav.customer.employees');
    if (path.includes('/customer/reports')) return t('nav.customer.reports');
    if (path.includes('/customer/approvals')) return t('nav.customer.approvals');
    if (path.includes('/customer/company')) return t('nav.customer.company');

    return 'Customer Portal';
  };

  // Close sidebar when clicking outside on mobile
  const handleOverlayClick = () => {
    setIsSidebarOpen(false);
  };

  // User initials for avatar
  const getUserInitials = () => {
    if (!currentUser) return 'C';

    const firstName = currentUser.firstName || '';
    const lastName = currentUser.lastName || '';

    return `${firstName.charAt(0)}${lastName.charAt(0)}`.toUpperCase();
  };

  return (
    <LayoutContainer>
      {/* Sidebar */}
      <Sidebar isOpen={isSidebarOpen}>
        <SidebarHeader>
          <SidebarLogo>
            <Logo height={34} />
            <SidebarLogoText>{t('common.appName')}</SidebarLogoText>
          </SidebarLogo>
        </SidebarHeader>

        <SidebarContent>
          <NavMenu>
            <NavSection>
              <NavSectionTitle>Main</NavSectionTitle>
              <NavItem to="/customer/dashboard" onClick={() => setIsSidebarOpen(false)}>
                <DashboardIcon />
                {t('nav.customer.dashboard')}
              </NavItem>
              <NavItem to="/customer/approvals" onClick={() => setIsSidebarOpen(false)}>
                <ApprovalIcon />
                {t('nav.customer.approvals')}
              </NavItem>
            </NavSection>

            <NavSection>
              <NavSectionTitle>Account</NavSectionTitle>
              <NavItem to="/customer/company" onClick={() => setIsSidebarOpen(false)}>
                <CompanyIcon />
                {t('nav.customer.company')}
              </NavItem>
              <NavItem to="/customer/notification-preferences" onClick={() => setIsSidebarOpen(false)}>
                <CompanyIcon />
                {t('nav.customer.notifications')}
              </NavItem>
              <NavItem as="a" href={guideHref('customer', i18n.language)} target="_blank" rel="noopener noreferrer" onClick={() => setIsSidebarOpen(false)}>
                <CompanyIcon />
                {t('common.userGuide')}
              </NavItem>
              <NavItem as="a" href="/legal" target="_blank" rel="noopener noreferrer" onClick={() => setIsSidebarOpen(false)}>
                <CompanyIcon />
                {t('legal.navLabel')}
              </NavItem>
            </NavSection>
          </NavMenu>
        </SidebarContent>

        <SidebarFooter>
          {t('common.copyright', { year: new Date().getFullYear() })}
        </SidebarFooter>
      </Sidebar>

      {/* Mobile overlay */}
      <Overlay isOpen={isSidebarOpen} onClick={handleOverlayClick} />

      {/* Content area */}
      <Content>
        <Header>
          <div style={{ display: 'flex', alignItems: 'center' }}>
            <MobileMenuToggle onClick={() => setIsSidebarOpen(!isSidebarOpen)}>
              <MenuIcon />
            </MobileMenuToggle>
            <PageTitle>{getPageTitle()}</PageTitle>
          </div>

          <UserInfo>
            <span style={{ marginRight: '1rem' }}>
              <LanguageSwitcher />
            </span>

            <NotificationIcon />

            <UserName>
              {currentUser?.firstName} {currentUser?.lastName}
            </UserName>
          </UserInfo>
        </Header>

        <MainContent>
          <Outlet />
        </MainContent>
        <AppFooter />
      </Content>

      {/* Bottom tab bar (mobile only) */}
      <BottomTabBar>
        <TabItem to="/customer/dashboard">
          <DashboardIcon />
          <TabLabel>{t('nav.customer.dashboard')}</TabLabel>
        </TabItem>
        <TabItem to="/customer/approvals">
          <ApprovalIcon />
          <TabLabel>{t('nav.customer.approvals')}</TabLabel>
        </TabItem>
        <TabItem to="/customer/company">
          <CompanyIcon />
          <TabLabel>{t('nav.customer.company')}</TabLabel>
        </TabItem>
      </BottomTabBar>
    </LayoutContainer>
  );
};

export default CustomerLayout;